import {Tooltip} from '@mui/material';
import {useMemo} from 'react';

import {TabKind, TenantChip, UserRow, expiryLabel} from '../lib/aggregate';
import {chipKindClass, legendSwatchClass} from '../lib/chip-style';

interface Props {
    rows: UserRow[];
    /** Other-kind chips for the same users — used to detect split-diagonal (both kinds for one cell). */
    otherKindRows: UserRow[];
    activeKind: TabKind;
    onCellClick?: (email: string, chip: TenantChip) => void;
}

interface MatrixTenant {
    resource: string;
    label: string;
}

function tenantsAcross(rowsA: UserRow[], rowsB: UserRow[]): MatrixTenant[] {
    const nameByResource = new Map<string, string | undefined>();
    for (const r of [...rowsA, ...rowsB]) {
        for (const c of r.chips) {
            if (!nameByResource.has(c.resource)) {
                nameByResource.set(c.resource, c.resourceName);
            } else if (!nameByResource.get(c.resource) && c.resourceName) {
                nameByResource.set(c.resource, c.resourceName);
            }
        }
    }
    const items: MatrixTenant[] = Array.from(nameByResource.entries()).map(([resource, name]) => ({
        resource,
        label: resource === '*' ? 'wildcard' : name ?? resource,
    }));
    items.sort((a, b) => {
        if (a.resource === '*') {
            return -1;
        }
        if (b.resource === '*') {
            return 1;
        }
        return a.label.localeCompare(b.label);
    });
    return items;
}

function chipCellLabel(chip: TenantChip): string {
    if (chip.permanent) {
        return '∞';
    }
    if (chip.daysRemaining < 0) {
        return '×';
    }
    return expiryLabel(chip.daysRemaining);
}

function chipCellTitle(chip: TenantChip): string {
    const head = chip.resourceName ? `${chip.resourceName} · ${chip.resource}` : chip.resource;
    if (chip.permanent) {
        return `${head} · permanent`;
    }
    if (chip.daysRemaining < 0) {
        return `${head} · expired`;
    }
    return `${head} · ${chip.daysRemaining}d remaining`;
}

export function MatrixView({rows, otherKindRows, activeKind, onCellClick}: Props) {
    // The legend swatch takes the active application's colour. On the combined
    // Users/Tenants tabs there is no single application, so fall back to neutral.
    const legendKindCls =
        activeKind === 'users' || activeKind === 'tenants' ? 'data' : legendSwatchClass(activeKind);
    const tenants = useMemo(() => tenantsAcross(rows, []), [rows]);
    const otherIndex = useMemo(() => {
        const map = new Map<string, Set<string>>();
        for (const r of otherKindRows) {
            map.set(r.email, new Set(r.chips.map(c => c.resource)));
        }
        return map;
    }, [otherKindRows]);

    if (rows.length === 0 || tenants.length === 0) {
        return (
            <div className="card">
                <div className="empty">
                    <h3>No matching grants</h3>
                    <div>Try clearing filters or selecting another tab.</div>
                </div>
            </div>
        );
    }

    return (
        <div className="card">
            <div className="mtx-wrap">
                <table className="mtx">
                    <thead>
                        <tr>
                            <th className="col-user-mx">User</th>
                            {tenants.map(t => (
                                <th key={t.resource} className="tenant-h" title={t.resource}>
                                    <span className="label">{t.label}</span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => {
                            const chipByTenant = new Map(row.chips.map(c => [c.resource, c]));
                            const otherTenants = otherIndex.get(row.email) ?? new Set<string>();
                            return (
                                <tr key={row.email}>
                                    <td className="col-user-mx">
                                        <div className="user-cell">
                                            <div className="meta">
                                                <div className="name">{row.name || row.email}</div>
                                                <div className="email">{row.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    {tenants.map(t => {
                                        const chip = chipByTenant.get(t.resource);
                                        const both = !!chip && otherTenants.has(t.resource);
                                        if (!chip) {
                                            return <td key={t.resource} className="cell" />;
                                        }
                                        const kindCls = chipKindClass(chip.kind);
                                        const stateCls =
                                            chip.permanent
                                                ? 'permanent'
                                                : chip.daysRemaining < 0
                                                ? 'expired'
                                                : '';
                                        return (
                                            <td key={t.resource} className={`cell${both ? ' both' : ''}`}>
                                                <Tooltip title={chipCellTitle(chip)} placement="top" arrow>
                                                    <span
                                                        className={`dot ${kindCls} ${stateCls}`.trim()}
                                                        onClick={() => onCellClick?.(row.email, chip)}
                                                        role="button"
                                                        tabIndex={0}
                                                    >
                                                        {chipCellLabel(chip)}
                                                    </span>
                                                </Tooltip>
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="legend">
                <span className="grp">
                    <span className={`sw ${legendKindCls}`}>∞</span>
                    <span>permanent</span>
                </span>
                <span className="grp">
                    <span className={`sw ${legendKindCls}`}>30</span>
                    <span>days remaining</span>
                </span>
                <span className="grp">
                    <span className="sw exp">×</span>
                    <span>expired</span>
                </span>
                <span className="grp">
                    <span
                        className="sw"
                        style={{
                            background:
                                'linear-gradient(135deg, rgba(14,165,255,0.22) 0 50%, rgba(168,60,200,0.22) 50% 100%)',
                        }}
                    />
                    <span>both kinds</span>
                </span>
            </div>
        </div>
    );
}
